// @section 1 Target Firms — Atlas /targets (BD capture surface).
//
// Daniel Diaz (and the BD team) capture every firm they intend to target. The
// record button (VoiceRecorder, Web Speech API) dictates a memo that pre-fills the
// business-model field; the operator confirms firm name + vertical and saves.
// Shared team view: all firms are visible to everyone. Demo building stays
// Adam-controlled — the demo_note field is where the demo link/status is recorded.

import { useCallback, useEffect, useMemo, useState } from 'react';
import VoiceRecorder from '../components/VoiceRecorder';
import {
  TargetFirm,
  TargetStatus,
  listTargets,
  createTarget,
  patchTarget,
} from '../api';

// @subsection 1.1 Status metadata (mirrors the hq_bd.target_firms CHECK).
const STATUSES: TargetStatus[] = ['identified', 'demo_ready', 'shown', 'next_step'];
const STATUS_LABEL: Record<TargetStatus, string> = {
  identified: 'Identified',
  demo_ready: 'Demo ready',
  shown: 'Shown',
  next_step: 'Next step',
};
const STATUS_DOT: Record<TargetStatus, string> = {
  identified: 'var(--tx-3)',
  demo_ready: 'var(--info)',
  shown: 'var(--brand-3)',
  next_step: 'var(--pos)',
};

// Known demo-library verticals (free text still allowed).
const VERTICALS = ['cpa', 'cpa-acquisition', 'contractor', 'restaurant'];

// @section 2 Component
export default function Targets() {
  const [firms, setFirms] = useState<TargetFirm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const refresh = useCallback(async () => {
    try { setFirms(await listTargets(statusFilter ? { status: statusFilter } : undefined)); }
    catch (e) { setError(String(e)); }
  }, [statusFilter]);

  useEffect(() => { void refresh(); }, [refresh]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { total: firms?.length ?? 0 };
    for (const s of STATUSES) c[s] = firms?.filter((f) => f.status === s).length ?? 0;
    return c;
  }, [firms]);

  if (error) return <div className="v5-page" style={{ padding: 24, color: 'var(--neg)' }}>API error: {error}</div>;

  return (
    <div className="v5-page" style={{ padding: 24, maxWidth: 1100 }}>
      <div className="page-head" style={{ padding: 0 }}>
        <div className="crumb"><span>Atlas</span><span className="sep">›</span><b>Target Firms</b></div>
        <div className="page-title">
          <h1>Target Firms</h1>
          <div className="sub">
            {counts.total} firms · {counts.identified} identified · {counts.demo_ready} demo-ready · {counts.shown} shown · {counts.next_step} next-step
          </div>
        </div>
      </div>

      <NewTargetForm onCreated={refresh} />

      {/* @block 2.1 Filter strip */}
      <div style={{ display: 'flex', gap: 8, margin: '20px 0 12px', flexWrap: 'wrap' }}>
        <FilterChip active={statusFilter === ''} onClick={() => setStatusFilter('')} label={`All (${counts.total})`} />
        {STATUSES.map((s) => (
          <FilterChip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} label={`${STATUS_LABEL[s]} (${counts[s]})`} />
        ))}
      </div>

      {/* @block 2.2 Firm list */}
      {!firms ? (
        <div style={{ color: 'var(--tx-3)' }}>Loading…</div>
      ) : firms.length === 0 ? (
        <div style={{ color: 'var(--tx-4)', fontStyle: 'italic', padding: '16px 0' }}>
          No target firms yet. Hit “Record memo” above and describe the first one.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {firms.map((f) => <FirmCard key={f.id} firm={f} onChanged={refresh} />)}
        </div>
      )}
    </div>
  );
}

// @section 3 New Target form
function NewTargetForm({ onCreated }: { onCreated: () => void }) {
  const [firmName, setFirmName] = useState('');
  const [vertical, setVertical] = useState('');
  const [businessModel, setBusinessModel] = useState('');
  const [relationshipSource, setRelationshipSource] = useState('');
  const [transcript, setTranscript] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onTranscript = useCallback((text: string) => {
    setTranscript((prev) => (prev ? `${prev} ${text}` : text));
    // Pre-fill the business-model field if empty; the operator edits + splits out the rest.
    setBusinessModel((prev) => (prev ? prev : text));
  }, []);

  const reset = () => {
    setFirmName(''); setVertical(''); setBusinessModel('');
    setRelationshipSource(''); setTranscript('');
  };

  const save = async () => {
    if (!firmName.trim()) { setErr('Firm name is required.'); return; }
    setSaving(true); setErr(null);
    try {
      await createTarget({
        firmName: firmName.trim(),
        vertical: vertical.trim() || undefined,
        businessModel: businessModel.trim() || undefined,
        relationshipSource: relationshipSource.trim() || undefined,
        voiceTranscript: transcript.trim() || undefined,
      });
      reset();
      onCreated();
    } catch (e) { setErr(String(e)); }
    finally { setSaving(false); }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: '1px solid var(--line)', background: 'var(--bg-1, rgba(255,255,255,0.03))',
    color: 'var(--tx-1)', fontSize: 13,
  };

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 16, marginTop: 16, background: 'var(--bg-1, rgba(255,255,255,0.02))' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <strong style={{ fontSize: 14 }}>New target firm</strong>
        <VoiceRecorder onTranscript={onTranscript} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={{ fontSize: 11, color: 'var(--tx-3)' }}>
          Firm name *
          <input style={inputStyle} value={firmName} onChange={(e) => setFirmName(e.target.value)} placeholder="e.g. Paldino Company CPA" />
        </label>
        <label style={{ fontSize: 11, color: 'var(--tx-3)' }}>
          Vertical
          <input style={inputStyle} value={vertical} onChange={(e) => setVertical(e.target.value)} list="target-verticals" placeholder="cpa / contractor / restaurant …" />
          <datalist id="target-verticals">{VERTICALS.map((v) => <option key={v} value={v} />)}</datalist>
        </label>
        <label style={{ fontSize: 11, color: 'var(--tx-3)', gridColumn: '1 / span 2' }}>
          Business model {transcript && <span style={{ color: 'var(--tx-4)' }}>(pre-filled from your memo — edit freely)</span>}
          <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={businessModel} onChange={(e) => setBusinessModel(e.target.value)} placeholder="What the firm does / how it makes money" />
        </label>
        <label style={{ fontSize: 11, color: 'var(--tx-3)', gridColumn: '1 / span 2' }}>
          Who you know there
          <input style={inputStyle} value={relationshipSource} onChange={(e) => setRelationshipSource(e.target.value)} placeholder="relationship / source" />
        </label>
      </div>

      {err && <div style={{ color: 'var(--neg)', fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={save} disabled={saving}
          style={{ padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'var(--brand-3)', color: 'var(--bg-0, #0b0b0c)', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save target'}
        </button>
      </div>
    </div>
  );
}

// @section 4 Firm card (inline status advance + demo note)
function FirmCard({ firm, onChanged }: { firm: TargetFirm; onChanged: () => void }) {
  const [demoNote, setDemoNote] = useState(firm.demoNote ?? '');
  const [savingNote, setSavingNote] = useState(false);

  const setStatus = async (status: TargetStatus) => {
    try { await patchTarget(firm.id, { status }); onChanged(); } catch { /* ignore */ }
  };
  const saveNote = async () => {
    setSavingNote(true);
    try { await patchTarget(firm.id, { demoNote }); onChanged(); } finally { setSavingNote(false); }
  };

  return (
    <div style={{ border: '1px solid var(--line)', borderLeft: `3px solid ${STATUS_DOT[firm.status]}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{firm.firmName}</div>
          <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 2 }}>
            {firm.vertical && <span style={{ marginRight: 8 }}>◦ {firm.vertical}</span>}
            {firm.relationshipSource && <span style={{ marginRight: 8 }}>via {firm.relationshipSource}</span>}
            {firm.ownerEmail && <span style={{ color: 'var(--tx-4)' }}>· {firm.ownerEmail.split('@')[0]}</span>}
          </div>
          {firm.businessModel && <div style={{ fontSize: 12, color: 'var(--tx-2)', marginTop: 6, maxWidth: 640 }}>{firm.businessModel}</div>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {STATUSES.map((s) => (
            <button key={s} type="button" onClick={() => setStatus(s)} title={STATUS_LABEL[s]}
              style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                padding: '3px 7px', borderRadius: 4, cursor: 'pointer',
                border: firm.status === s ? `1px solid ${STATUS_DOT[s]}` : '1px solid var(--line)',
                background: firm.status === s ? STATUS_DOT[s] : 'transparent',
                color: firm.status === s ? 'var(--bg-0, #0b0b0c)' : 'var(--tx-3)',
              }}>
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Adam-controlled demo note */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <input
          value={demoNote}
          onChange={(e) => setDemoNote(e.target.value)}
          placeholder="Demo note (link / which demo / status) — Adam-controlled"
          style={{ flex: 1, padding: '6px 9px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--bg-1, rgba(255,255,255,0.03))', color: 'var(--tx-1)', fontSize: 12 }}
        />
        {demoNote !== (firm.demoNote ?? '') && (
          <button type="button" onClick={saveNote} disabled={savingNote}
            style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--line)', cursor: 'pointer', background: 'transparent', color: 'var(--tx-2)' }}>
            {savingNote ? '…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}

// @section 5 Small UI helpers
function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        fontSize: 12, padding: '5px 11px', borderRadius: 999, cursor: 'pointer',
        border: active ? '1px solid var(--brand-3)' : '1px solid var(--line)',
        background: active ? 'var(--brand-3)' : 'transparent',
        color: active ? 'var(--bg-0, #0b0b0c)' : 'var(--tx-3)',
      }}>
      {label}
    </button>
  );
}
